import { computed, type ComputedRef } from 'vue'

export interface AppEnv {
  /** Short label shown in the pill (e.g. "Local", "Sandbox"). */
  badgeText: string
  /** Longer description shown in the tooltip. */
  tooltip: string
}

export type InnovatorPlane = 'staging' | 'production'

function browserHostname(): string {
  if (typeof window === 'undefined') return ''
  return window.location.hostname
}

/** True when the SPA is the staging (sandbox) core admin. */
export function isSandboxCoreHost(hostname = browserHostname()): boolean {
  return hostname === 'staging.rukherp.com'
}

/**
 * Public innovator host for a phone-book plane.
 * Prefer an explicit plane (prod SPA tabs); otherwise derive from browser host.
 */
export function innovatorPublicHost(
  subdomain: string,
  planeOrHostname?: InnovatorPlane | string
): string {
  const slug = subdomain.trim().toLowerCase()

  if (planeOrHostname === 'staging') {
    return `${slug}-staging.rukherp.com`
  }
  if (planeOrHostname === 'production') {
    return `${slug}.rukherp.com`
  }

  const host = planeOrHostname && planeOrHostname.includes('.')
    ? planeOrHostname
    : browserHostname()

  if (isSandboxCoreHost(host)) {
    return `${slug}-staging.rukherp.com`
  }
  return `${slug}.rukherp.com`
}

/**
 * Resolves the current deployment environment from the browser hostname.
 *
 * Returns null for production (and any unrecognized host) so callers can hide
 * the badge entirely outside local/staging environments.
 */
export function useAppEnv(): ComputedRef<AppEnv | null> {
  return computed((): AppEnv | null => {
    const host = browserHostname()
    if (!host) return null
    if (host === 'localhost' || host === '127.0.0.1') {
      return { badgeText: 'Local', tooltip: 'Local' }
    }
    if (isSandboxCoreHost(host)) {
      return { badgeText: 'Sandbox', tooltip: 'Sandbox Site' }
    }
    return null
  })
}
